// frontend/src/components/current-load/CurrentLoadDetailsPanel.jsx

import React, {
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import Select from "react-select";

import {
  Boxes,
  CheckCircle2,
  ClipboardList,
  FileText,
  Mail,
  Package,
  Pencil,
  Truck,
  Weight,
} from "lucide-react";

const CURRENCY_OPTIONS = [
  {
    value: "EUR",
    label: "EUR",
  },
  {
    value: "USD",
    label: "USD",
  },
  {
    value: "GBP",
    label: "GBP",
  },
];

function safeDocumentBody() {
  return typeof document !== "undefined"
    ? document.body
    : null;
}

function formatNumber(
  value,
  locale,
  undefinedLabel,
  maximumFractionDigits = 3
) {
  const numberValue =
    Number(value);

  if (
    !Number.isFinite(
      numberValue
    )
  ) {
    return undefinedLabel;
  }

  return numberValue.toLocaleString(
    locale,
    {
      maximumFractionDigits,
    }
  );
}

function formatCurrency(
  value,
  currency,
  locale,
  undefinedLabel
) {
  const numberValue =
    Number(
      String(
        value ??
        ""
      )
        .trim()
        .replace(",", ".")
    );

  if (
    !Number.isFinite(
      numberValue
    )
  ) {
    return undefinedLabel;
  }

  try {
    return new Intl.NumberFormat(
      locale,
      {
        style: "currency",
        currency:
          currency ||
          "EUR",
      }
    ).format(
      numberValue
    );
  } catch {
    return `${numberValue.toLocaleString(
      locale,
      {
        minimumFractionDigits:
          2,

        maximumFractionDigits:
          2,
      }
    )} ${currency || "EUR"}`;
  }
}

function emailListLabel(
  options,
  undefinedLabel
) {
  const values =
    (
      Array.isArray(options)
        ? options
        : []
    )
      .map((option) =>
        String(
          option?.label ||
          option?.value ||
          ""
        ).trim()
      )
      .filter(Boolean);

  return values.length > 0
    ? values.join(", ")
    : undefinedLabel;
}

export default function CurrentLoadDetailsPanel({
  weight,
  merchandise,
  length,
  width,
  height,
  trailerType,
  trailerTypeOther,
  price,
  priceCurrency,
  price4subcontracted,
  notes4driver,
  setNotes4driver,
  isShipmentRequestLoad = false,
  clientType,
  clientTypeOptions = [],
  selectedClient,
  clientOptions = [],
  selectedClientContactEmails = [],
  selectedClientContactEmailOptions = [],
  transportCarrier,
  transportCarrierOptions = [],
  showSubcontractedFields,
  selectedSubcontractedEmails = [],
  selectedSubcontractedEmailOptions = [],
  trailerTypeOptions = [],
  setWeight,
  setMerchandise,
  setTrailerType,
  setTrailerTypeOther,
  setPrice,
  setPriceCurrency,
  setPrice4subcontracted,
  setClientType,
  setSelectedClient,
  setSelectedClientContactEmails,
  setTransportCarrier,
  setSelectedSubcontractedEmails,
  merchandiseError,
  weightError,
  lengthError,
  widthError,
  heightError,
  priceError,
  price4subcontractedError,
  trailerTypeError,
  trailerTypeOtherError,
  clientTypeError,
  clientError,
  transportCarrierError,
  setMerchandiseError,
  setWeightError,
  setPriceError,
  setPrice4subcontractedError,
  setTrailerTypeError,
  setTrailerTypeOtherError,
  setClientTypeError,
  setClientError,
  setTransportCarrierError,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "edit_load"
    );

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt";

  const [
    isEditing,
    setIsEditing,
  ] =
    useState(false);

  const snapshotRef =
    useRef(null);

  const undefinedLabel =
    t(
      "common.undefined",
      {
        defaultValue:
          "Não definido",
      }
    );

  const clientTypeLabel =
    useMemo(
      () =>
        clientTypeOptions.find(
          (option) =>
            option.value ===
            clientType
        )?.label ||
        undefinedLabel,
      [
        clientType,
        clientTypeOptions,
        undefinedLabel,
      ]
    );

  const trailerLabel =
    useMemo(
      () => {
        const option =
          trailerTypeOptions.find(
            (item) =>
              String(item.value) ===
              String(
                trailerType ||
                ""
              )
          );

        if (
          String(
            trailerType ||
            ""
          )
            .trim()
            .toUpperCase() ===
            "OTHER" &&
          trailerTypeOther
        ) {
          return `${
            option?.label ||
            trailerType
          } · ${trailerTypeOther}`;
        }

        return (
          option?.label ||
          trailerType ||
          undefinedLabel
        );
      },
      [
        trailerType,
        trailerTypeOther,
        trailerTypeOptions,
        undefinedLabel,
      ]
    );

  const dimensions = [
    length,
    width,
    height,
  ]
    .map((value) =>
      Number.isFinite(
        Number(value)
      )
        ? Number(
            value
          ).toFixed(2)
        : null
    )
    .filter(Boolean);

  const beginEditing =
    () => {
      snapshotRef.current = {
        weight,
        merchandise,
        trailerType,
        trailerTypeOther,
        price,
        priceCurrency,
        price4subcontracted,
        notes4driver,
        clientType,
        selectedClient,

        selectedClientContactEmails:
          Array.isArray(
            selectedClientContactEmails
          )
            ? [
                ...selectedClientContactEmails,
              ]
            : [],

        transportCarrier,

        selectedSubcontractedEmails:
          Array.isArray(
            selectedSubcontractedEmails
          )
            ? [
                ...selectedSubcontractedEmails,
              ]
            : [],
      };

      setIsEditing(true);
    };

  const cancelEditing =
    () => {
      const snapshot =
        snapshotRef.current;

      if (snapshot) {
        setWeight?.(
          snapshot.weight
        );

        setMerchandise?.(
          snapshot.merchandise
        );

        setTrailerType?.(
          snapshot.trailerType
        );

        setTrailerTypeOther?.(
          snapshot.trailerTypeOther
        );

        setPrice?.(
          snapshot.price
        );

        setPriceCurrency?.(
          snapshot.priceCurrency
        );

        setPrice4subcontracted?.(
          snapshot.price4subcontracted
        );

        setNotes4driver?.(
          snapshot.notes4driver
        );

        setClientType?.(
          snapshot.clientType
        );

        setSelectedClient?.(
          snapshot.selectedClient
        );

        setSelectedClientContactEmails?.(
          snapshot.selectedClientContactEmails
        );

        setTransportCarrier?.(
          snapshot.transportCarrier
        );

        setSelectedSubcontractedEmails?.(
          snapshot.selectedSubcontractedEmails
        );
      }

      snapshotRef.current =
        null;

      setIsEditing(false);
    };

  const finishEditing =
    () => {
      snapshotRef.current =
        null;

      setIsEditing(false);
    };

  return (
    <section className="shipment-v2-panel shipment-v2-load-panel current-load-details-panel">
      <div className="shipment-v2-panel-header">
        <h2>
          {t(
            "workspace.detailsTitle",
            {
              defaultValue:
                "Detalhes da carga",
            }
          )}
        </h2>

        {isEditing ? (
          <div className="edit-request-load-details-actions">
            <button
              type="button"
              className="edit-request-load-details-cancel-btn"
              onClick={cancelEditing}
            >
              {t(
                "buttons.cancel"
              )}
            </button>

            <button
              type="button"
              className="edit-request-load-details-save-btn"
              onClick={finishEditing}
            >
              <CheckCircle2
                size={15}
              />

              {t(
                "workspace.finishEditing",
                {
                  defaultValue:
                    "Concluir edição",
                }
              )}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="shipment-v2-plain-action edit-request-load-details-edit-btn"
            onClick={beginEditing}
          >
            <Pencil
              size={13}
            />

            {t(
              "workspace.editDetails",
              {
                defaultValue:
                  "Editar detalhes",
              }
            )}
          </button>
        )}
      </div>

      <>
        <div className="shipment-v2-load-top-grid current-load-details-overview-grid">
          <div className="shipment-v2-mini-table edit-request-load-details-table">
            <div>
              <span>
                <Package
                  size={14}
                />

                {t(
                  "fields.merchandise"
                )}
              </span>

              {isEditing ? (
                <div className="current-load-seamless-field">
                  <input
                    type="text"
                    className="edit-request-load-details-input"
                    value={
                      merchandise ||
                      ""
                    }
                    onChange={(event) => {
                      setMerchandise?.(
                        event.target.value
                      );

                      setMerchandiseError?.(
                        ""
                      );
                    }}
                  />

                  {merchandiseError && (
                    <small className="pendent-workspace-editor-error current-load-seamless-error">
                      {merchandiseError}
                    </small>
                  )}
                </div>
              ) : (
                <strong>
                  {merchandise ||
                    undefinedLabel}
                </strong>
              )}
            </div>

            <div>
              <span>
                <Boxes
                  size={14}
                />

                {t(
                  "fields.loadDimensions"
                )}
              </span>

              {isEditing ? (
                <div className="current-load-seamless-field">
                  {dimensions.length ===
                  3 ? (
                    <div className="edit-request-load-dimensions is-readonly">
                      <span className="edit-request-load-dimension-value">
                        {dimensions[0]}
                      </span>

                      <span className="edit-request-load-dimension-separator">
                        ×
                      </span>

                      <span className="edit-request-load-dimension-value">
                        {dimensions[1]}
                      </span>

                      <span className="edit-request-load-dimension-separator">
                        ×
                      </span>

                      <span className="edit-request-load-dimension-value">
                        {dimensions[2]}
                      </span>

                      <small className="edit-request-load-dimension-unit">
                        m
                      </small>
                    </div>
                  ) : (
                    <strong>
                      {undefinedLabel}
                    </strong>
                  )}

                  {(lengthError ||
                    widthError ||
                    heightError) && (
                    <small className="pendent-workspace-editor-error current-load-seamless-error">
                      {lengthError ||
                        widthError ||
                        heightError}
                    </small>
                  )}
                </div>
              ) : dimensions.length ===
                3 ? (
                <div className="edit-request-load-dimensions is-readonly">
                  <span className="edit-request-load-dimension-value">
                    {dimensions[0]}
                  </span>

                  <span className="edit-request-load-dimension-separator">
                    ×
                  </span>

                  <span className="edit-request-load-dimension-value">
                    {dimensions[1]}
                  </span>

                  <span className="edit-request-load-dimension-separator">
                    ×
                  </span>

                  <span className="edit-request-load-dimension-value">
                    {dimensions[2]}
                  </span>

                  <small className="edit-request-load-dimension-unit">
                    m
                  </small>
                </div>
              ) : (
                <strong>
                  {undefinedLabel}
                </strong>
              )}
            </div>

            <div>
              <span>
                <Weight
                  size={14}
                />

                {t(
                  "fields.weight"
                )}
              </span>

              {isEditing ? (
                <div className="current-load-seamless-field">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="edit-request-load-details-input"
                    value={
                      weight ??
                      ""
                    }
                    onChange={(event) => {
                      setWeight?.(
                        event.target.value
                      );

                      setWeightError?.(
                        ""
                      );
                    }}
                  />

                  {weightError && (
                    <small className="pendent-workspace-editor-error current-load-seamless-error">
                      {weightError}
                    </small>
                  )}
                </div>
              ) : (
                <strong>
                  {formatNumber(
                    weight,
                    locale,
                    undefinedLabel
                  )}
                </strong>
              )}
            </div>

            <div>
              <span>
                <Truck
                  size={14}
                />

                {t(
                  "trailerType.label"
                )}
              </span>

              {isEditing ? (
                <div className="current-load-seamless-field">
                  <Select
                    isClearable
                    options={
                      trailerTypeOptions
                    }
                    value={
                      trailerTypeOptions.find(
                        (option) =>
                          option.value ===
                          trailerType
                      ) ||
                      null
                    }
                    onChange={(option) => {
                      const nextValue =
                        option?.value ||
                        null;

                      setTrailerType?.(
                        nextValue
                      );

                      setTrailerTypeError?.(
                        ""
                      );

                      setTrailerTypeOtherError?.(
                        ""
                      );

                      if (
                        nextValue !==
                        "OTHER"
                      ) {
                        setTrailerTypeOther?.(
                          ""
                        );
                      }
                    }}
                    placeholder={t(
                      "trailerType.placeholder"
                    )}
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    className="current-load-seamless-select"
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={safeDocumentBody()}
                    menuPosition="fixed"
                  />

                  {trailerTypeError && (
                    <small className="pendent-workspace-editor-error current-load-seamless-error">
                      {trailerTypeError}
                    </small>
                  )}
                </div>
              ) : (
                <strong>
                  {trailerLabel}
                </strong>
              )}
            </div>

            {isEditing &&
              String(
                trailerType ||
                ""
              )
                .trim()
                .toUpperCase() ===
                "OTHER" && (
                <div>
                  <span>
                    <Truck
                      size={14}
                    />

                    {t(
                      "trailerType.otherLabel"
                    )}
                  </span>

                  <div className="current-load-seamless-field">
                    <input
                      type="text"
                      className="edit-request-load-details-input"
                      value={
                        trailerTypeOther ||
                        ""
                      }
                      onChange={(event) => {
                        setTrailerTypeOther?.(
                          event.target.value
                        );

                        setTrailerTypeOtherError?.(
                          ""
                        );
                      }}
                    />

                    {trailerTypeOtherError && (
                      <small className="pendent-workspace-editor-error current-load-seamless-error">
                        {trailerTypeOtherError}
                      </small>
                    )}
                  </div>
                </div>
              )}

            <div>
              <span>
                <ClipboardList
                  size={14}
                />

                {t(
                  "fields.clientType"
                )}
              </span>

              {isEditing ? (
                <div className="current-load-seamless-field">
                  <Select
                    isClearable
                    isSearchable={false}
                    options={
                      clientTypeOptions
                    }
                    value={
                      clientTypeOptions.find(
                        (option) =>
                          option.value ===
                          clientType
                      ) ||
                      null
                    }
                    onChange={(option) => {
                      setClientType?.(
                        option?.value ||
                        ""
                      );

                      setClientTypeError?.(
                        ""
                      );
                    }}
                    placeholder={t(
                      "placeholders.selectClientType"
                    )}
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    className="current-load-seamless-select"
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={safeDocumentBody()}
                    menuPosition="fixed"
                  />

                  {clientTypeError && (
                    <small className="pendent-workspace-editor-error current-load-seamless-error">
                      {clientTypeError}
                    </small>
                  )}
                </div>
              ) : (
                <strong>
                  {clientTypeLabel}
                </strong>
              )}
            </div>

            <div>
              <span>
                <ClipboardList
                  size={14}
                />

                {clientType ===
                "industry"
                  ? t(
                      "fields.industryClient"
                    )
                  : clientType ===
                    "carrier"
                    ? t(
                        "fields.carrierClient"
                      )
                    : t(
                        "workspace.client",
                        {
                          defaultValue:
                            "Cliente",
                        }
                      )}
              </span>

              {isEditing ? (
                <div className="current-load-seamless-field">
                  <Select
                    isClearable
                    isDisabled={
                      !clientType
                    }
                    options={clientOptions}
                    value={selectedClient}
                    onChange={(option) => {
                      setSelectedClient?.(
                        option ||
                        null
                      );

                      setClientError?.(
                        ""
                      );
                    }}
                    placeholder={t(
                      "placeholders.selectClient"
                    )}
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    className="current-load-seamless-select"
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={safeDocumentBody()}
                    menuPosition="fixed"
                  />

                  {clientError && (
                    <small className="pendent-workspace-editor-error current-load-seamless-error">
                      {clientError}
                    </small>
                  )}
                </div>
              ) : (
                <strong>
                  {selectedClient?.label ||
                    undefinedLabel}
                </strong>
              )}
            </div>
          </div>

          <div className="shipment-v2-mini-table current-load-commercial-table">
            <div>
              <span>
                {t(
                  "fields.price"
                )}
              </span>

              {isEditing ? (
                <div className="current-load-seamless-field">
                  <div className="current-load-seamless-price">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="edit-request-load-details-input"
                      value={
                        price ??
                        ""
                      }
                      onChange={(event) => {
                        setPrice?.(
                          event.target.value
                        );

                        setPriceError?.(
                          ""
                        );
                      }}
                    />

                    <Select
                      options={CURRENCY_OPTIONS}
                      value={
                        CURRENCY_OPTIONS.find(
                          (option) =>
                            option.value ===
                            (
                              priceCurrency ||
                              "EUR"
                            )
                        ) ||
                        CURRENCY_OPTIONS[0]
                      }
                      onChange={(option) =>
                        setPriceCurrency?.(
                          option?.value ||
                          "EUR"
                        )
                      }
                      isClearable={false}
                      isSearchable={false}
                      className="current-load-seamless-select current-load-seamless-currency-select"
                      classNamePrefix="current-load-workspace-select"
                      menuPortalTarget={safeDocumentBody()}
                      menuPosition="fixed"
                    />
                  </div>

                  {priceError && (
                    <small className="pendent-workspace-editor-error current-load-seamless-error">
                      {priceError}
                    </small>
                  )}
                </div>
              ) : (
                <strong>
                  {formatCurrency(
                    price,
                    priceCurrency,
                    locale,
                    undefinedLabel
                  )}
                </strong>
              )}
            </div>

            {showSubcontractedFields && (
              <div>
                <span>
                  {t(
                    "fields.price4subcontracted"
                  )}
                </span>

                {isEditing ? (
                  <div className="current-load-seamless-field">
                    <div className="current-load-seamless-price">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="edit-request-load-details-input"
                        value={
                          price4subcontracted ??
                          ""
                        }
                        onChange={(event) => {
                          setPrice4subcontracted?.(
                            event.target.value
                          );

                          setPrice4subcontractedError?.(
                            ""
                          );
                        }}
                      />

                      <Select
                        options={CURRENCY_OPTIONS}
                        value={
                          CURRENCY_OPTIONS.find(
                            (option) =>
                              option.value ===
                              (
                                priceCurrency ||
                                "EUR"
                              )
                          ) ||
                          CURRENCY_OPTIONS[0]
                        }
                        onChange={(option) =>
                          setPriceCurrency?.(
                            option?.value ||
                            "EUR"
                          )
                        }
                        isClearable={false}
                        isSearchable={false}
                        className="current-load-seamless-select current-load-seamless-currency-select"
                        classNamePrefix="current-load-workspace-select"
                        menuPortalTarget={safeDocumentBody()}
                        menuPosition="fixed"
                      />
                    </div>

                    {price4subcontractedError && (
                      <small className="pendent-workspace-editor-error current-load-seamless-error">
                        {price4subcontractedError}
                      </small>
                    )}
                  </div>
                ) : (
                  <strong>
                    {formatCurrency(
                      price4subcontracted,
                      priceCurrency,
                      locale,
                      undefinedLabel
                    )}
                  </strong>
                )}
              </div>
            )}

            <div>
              <span>
                {t(
                  "fields.transportCarrier"
                )}
              </span>

              {isEditing ? (
                <div className="current-load-seamless-field">
                  <Select
                    isClearable
                    options={
                      transportCarrierOptions
                    }
                    value={
                      transportCarrier
                    }
                    onChange={(option) => {
                      setTransportCarrier?.(
                        option ||
                        null
                      );

                      setTransportCarrierError?.(
                        ""
                      );
                    }}
                    placeholder={t(
                      "placeholders.selectTransportCarrier"
                    )}
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    className="current-load-seamless-select"
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={safeDocumentBody()}
                    menuPosition="fixed"
                  />

                  {transportCarrierError && (
                    <small className="pendent-workspace-editor-error current-load-seamless-error">
                      {transportCarrierError}
                    </small>
                  )}
                </div>
              ) : (
                <strong>
                  {transportCarrier?.label ||
                    undefinedLabel}
                </strong>
              )}
            </div>
          </div>
        </div>

        <div className="shipment-v2-load-bottom-grid current-load-contact-grid">
          <div className="shipment-v2-mini-table compact edit-request-load-details-table">
            <div>
              <span>
                <Mail
                  size={14}
                />

                {t(
                  "clientContactEmails.label"
                )}
              </span>

              {isEditing ? (
                <div className="current-load-seamless-field">
                  <Select
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    isDisabled={
                      !selectedClient?.value
                    }
                    options={
                      selectedClientContactEmailOptions
                    }
                    value={
                      selectedClientContactEmails
                    }
                    onChange={(selectedOptions) =>
                      setSelectedClientContactEmails?.(
                        Array.isArray(
                          selectedOptions
                        )
                          ? selectedOptions.slice(
                              0,
                              3
                            )
                          : []
                      )
                    }
                    placeholder={t(
                      "clientContactEmails.placeholder"
                    )}
                    noOptionsMessage={() =>
                      t(
                        "clientContactEmails.noOptions"
                      )
                    }
                    className="current-load-seamless-select current-load-seamless-select--multi"
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={safeDocumentBody()}
                    menuPosition="fixed"
                  />
                </div>
              ) : (
                <strong className="current-load-wrap-value">
                  {emailListLabel(
                    selectedClientContactEmails,
                    undefinedLabel
                  )}
                </strong>
              )}
            </div>

            {showSubcontractedFields && (
              <div>
                <span>
                  <Mail
                    size={14}
                  />

                  {t(
                    "subcontractedEmails.label"
                  )}
                </span>

                {isEditing ? (
                  <div className="current-load-seamless-field">
                    <Select
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      options={
                        selectedSubcontractedEmailOptions
                      }
                      value={
                        selectedSubcontractedEmails
                      }
                      onChange={(selectedOptions) =>
                        setSelectedSubcontractedEmails?.(
                          Array.isArray(
                            selectedOptions
                          )
                            ? selectedOptions.slice(
                                0,
                                3
                              )
                            : []
                        )
                      }
                      placeholder={t(
                        "subcontractedEmails.placeholder"
                      )}
                      noOptionsMessage={() =>
                        t(
                          "subcontractedEmails.noOptions"
                        )
                      }
                      className="current-load-seamless-select current-load-seamless-select--multi"
                      classNamePrefix="current-load-workspace-select"
                      menuPortalTarget={safeDocumentBody()}
                      menuPosition="fixed"
                    />
                  </div>
                ) : (
                  <strong className="current-load-wrap-value">
                    {emailListLabel(
                      selectedSubcontractedEmails,
                      undefinedLabel
                    )}
                  </strong>
                )}
              </div>
            )}
          </div>
        </div>

        {isShipmentRequestLoad && (
          <div className="shipment-v2-load-bottom-grid current-load-driver-note-grid">
            <div className="shipment-v2-mini-table compact edit-request-load-details-table current-load-driver-note-table">
              <div className="current-load-driver-note-row">
                <span>
                  <FileText
                    size={14}
                  />

                  {t(
                    "fields.notesForDriver"
                  )}
                </span>

                {isEditing ? (
                  <div className="current-load-seamless-field">
                    <textarea
                      rows={3}
                      maxLength={2000}
                      className="edit-request-load-notes-input current-load-note-inline-editor current-load-driver-note-textarea"
                      value={
                        notes4driver ||
                        ""
                      }
                      placeholder={t(
                        "placeholders.notesForDriver"
                      )}
                      onChange={(event) =>
                        setNotes4driver?.(
                          event.target.value
                        )
                      }
                    />

                    <small className="current-load-note-counter">
                      {String(
                        notes4driver ||
                        ""
                      ).length}
                      /2000
                    </small>
                  </div>
                ) : (
                  <strong className="current-load-driver-note-value">
                    {notes4driver ||
                      t(
                        "workspace.noDriverNotes",
                        {
                          defaultValue:
                            "Sem notas para o motorista.",
                        }
                      )}
                  </strong>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    </section>
  );
}